import React, { useCallback, useEffect, useRef, useState } from "react";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { useOptionalDependencyInjector } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import styles from "./audio-player.module.scss";
import classnames from "classnames";

const addHeadingZero = (num: number): string => {
  return num > 9 ? num.toString() : `0${num}`;
};

const readSignedUrlExpiry = (src: string): { ttlSec: number; urlAgeSec: number; expired: boolean } | undefined => {
  try {
    const params = new URL(src, window.location.href).searchParams;
    const signedAt = params.get("X-Amz-Date");
    const ttlSec = Number(params.get("X-Amz-Expires"));
    if (!signedAt || !Number.isFinite(ttlSec)) {
      return undefined;
    }
    const iso = signedAt.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, "$1-$2-$3T$4:$5:$6Z");
    const urlAgeSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (!Number.isFinite(urlAgeSec)) {
      return undefined;
    }
    return { ttlSec, urlAgeSec, expired: urlAgeSec > ttlSec };
  } catch {
    return undefined;
  }
};

const getDisplayTimeBySeconds = (seconds: number): string | undefined => {
  const min = Math.floor(seconds / 60);
  const minStr = addHeadingZero(min);
  const secStr = addHeadingZero(Math.floor(seconds % 60));

  return `${minStr}:${secStr}`;
};
const AudioControls: React.FC<{
  isPlaying: boolean;
  onPlayPauseClick: (isPlaying: boolean) => void;
}> = ({ isPlaying, onPlayPauseClick }) => (
  <span className={styles.audioControls}>
    {isPlaying ? (
      <SvgV2 onClick={() => onPlayPauseClick(false)} width={19} height={14} iconName={`pause`} />
    ) : (
      <SvgV2 onClick={() => onPlayPauseClick(true)} width={19} height={19} iconName={`play`} />
    )}
  </span>
);

export const AudioPlayer: React.FC<{ audioSrc: string; className?: string }> = ({ audioSrc, className }) => {
  const [trackProgress, setTrackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [durationMetadata, setDurationMetadata] = useState(NaN);

  const audioRef = useRef(new Audio(audioSrc));
  const intervalRef = useRef<any>(undefined);

  // Null wherever nothing bound it (the CRM widget), which keeps this component free of a hard DI
  // dependency. getLogger() is called inside the handler so it only allocates on an actual failure.
  const frontLogger = useOptionalDependencyInjector(FrontLogger);

  // The media element fails silently: a dead src rejects play() and fires "error", and neither was
  // observed before, so an unreachable recording left no trace at all (EN-7284). Never logs the src
  // -- a presigned URL is a working credential for that recording.
  const onLoadFailed = useCallback(() => {
    const expiry = readSignedUrlExpiry(audioRef.current.src);
    frontLogger?.getLogger("AudioPlayer").error(
      {
        expired: expiry?.expired ?? null,
        urlAgeSec: expiry?.urlAgeSec ?? null,
        ttlSec: expiry?.ttlSec ?? null,
        mediaErrorCode: audioRef.current.error?.code ?? null,
      },
      "media playback failed"
    );
  }, [frontLogger]);

  const startTimer = () => {
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (audioRef.current.ended) {
        setTrackProgress(0);
        audioRef.current.pause();
        setIsPlaying(false);
        clearInterval(intervalRef.current);
      } else {
        setTrackProgress(audioRef.current.currentTime);
      }
    }, 500);
  };

  const onScrub = (value: number) => {
    clearInterval(intervalRef.current);
    audioRef.current.currentTime = value;
    setTrackProgress(audioRef.current.currentTime);
  };

  const onScrubEnd = () => {
    if (!isPlaying) {
      onPlayPauseClick(true);
    }
    startTimer();
  };

  const onPlayPauseClick = useCallback(
    (isPlaying: boolean) => {
      setIsPlaying(isPlaying);

      if (isPlaying) {
        audioRef.current.play().catch(onLoadFailed);
        startTimer();
      } else {
        audioRef.current.pause();
      }
    },
    [onLoadFailed]
  );

  const getAudioTime = () => {
    if (isPlaying) {
      return getDisplayTimeBySeconds(trackProgress);
    }
    if (Number.isNaN(audioRef.current.duration)) {
      return "--:--";
    }
    return getDisplayTimeBySeconds(audioRef.current.duration);
  };

  useEffect(() => {
    function onLoadMetadata() {
      if (audioRef.current.duration === Infinity || isNaN(Number(audioRef.current.duration))) {
        audioRef.current.currentTime = 1e101;
        audioRef.current.addEventListener("timeupdate", getDuration);
      }
      setDurationMetadata(audioRef.current.duration);
    }
    function getDuration() {
      setDurationMetadata(audioRef.current.duration);
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener("timeupdate", getDuration);
    }

    audioRef.current.addEventListener("loadedmetadata", onLoadMetadata);
    audioRef.current.addEventListener("error", onLoadFailed);
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("timeupdate", getDuration);
        audioRef.current.removeEventListener("loadedmetadata", onLoadMetadata);
        audioRef.current.removeEventListener("error", onLoadFailed);
      }

      clearInterval(intervalRef.current);
    };
  }, [onLoadFailed]);

  return (
    <div className={classnames(styles.audioPlayer, className)}>
      <div className={styles.audioPlayerTop}>
        <AudioControls isPlaying={isPlaying} onPlayPauseClick={onPlayPauseClick} />

        <input
          type="range"
          value={trackProgress}
          step="1"
          min="0"
          max={durationMetadata ? durationMetadata : `${durationMetadata}`}
          className="progress"
          onChange={(e) => onScrub(Number(e.target.value))}
          onMouseUp={onScrubEnd}
          onKeyUp={onScrubEnd}
        />
      </div>

      <div className={styles.audioTime}>{getAudioTime()}</div>
    </div>
  );
};
